import React, { Fragment } from 'react';
import { Route, Switch } from 'react-router-dom';
import { withApollo } from 'react-apollo';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import delve from 'dlv';

import {
  AssessmentProvider,
  useAssessmentContext,
  SESSION_STATUS,
} from 'Context/AssessmentContext';
import { useQuizContext } from 'Context/QuizContext';
import { useConfig } from 'Context/ConfigContext';
import {
  submitClassroomQuizAnswer,
  submitClassroomQuizAnswers,
  gradeClassroomQuizExamPaper,
} from 'GraphQL/mutation/Classroom.mutation';
import { getClassroomQuizExamPaperById } from 'GraphQL/query/Classroom.query';
import { useCapacitor } from 'Util/Capacitors';
import { useQuizExceptionTracking } from 'Util/Analytics';
import { getMyExternalProfileInfo, getProfile } from 'GraphQL/query/MyProfile.query';
import GradedExamModal, {
  GRADED_EXAM_MODAL_VARIANT,
  GRADED_EXAM_MODAL_VARIANT_PROPS,
} from 'Components/Quiz/Components/GradedExamModal';
import { useClassroomContext } from 'Context/ClassroomContext';

import { QueryComponent } from '../../../../GraphQL';
import BlockNavigationDialog, {
  TimeoutBlockNavigationDialog,
} from '../../Components/BlockNavigationDialog';
import {
  SESSION_SUB_ROUTES,
  QUIZ_SUB_ROUTES,
  QUIZ_VARIANTS,
  EXAM_PAPER_ITEM_TYPE,
} from '../../constant';

const getError = (err) => {
  if (err) {
    if (err.networkError) {
      return err.networkError.result?.errors[0]?.message;
    }
    if (err.graphQLErrors?.length > 0) {
      return err.graphQLErrors[0]?.message;
    }
    return err.message ?? 'Unknown Error';
  }
  return 'Unknown Error';
};

const SessionRoute = withApollo(
  ({
    appVersion,
    id,
    providerName,
    onEndQuiz,
    onCompleted,
    history,
    match,
    location,
    client,
    t,
  }) => {
    const {
      classroom: { quiz },
    } = useConfig();
    const { logSubmitQuiz, logSubmitQuizError } = useQuizExceptionTracking();
    const { showResult = false } = quiz?.gradedExam ?? {};
    const {
      startSession,
      clear,
      gradePaper,
      status,
      setStatus,
      questions,
      timeLimit,
      setCurrentQuestionIndex,
      timeRemaining: secondsRemaining,
      hasRequestSubmitGradedExam,
      setRequestSubmitGradedExam,
    } = useAssessmentContext();
    const { variant, setError, setTimeSpent, metadata } = useQuizContext();
    const { examId, examSessionId } = match.params;
    const { title, type } = metadata;
    const { logFinalAnswersSubmitError, logGradePaperError } = useQuizExceptionTracking();
    const gradedExamTimeoutProps =
      GRADED_EXAM_MODAL_VARIANT_PROPS[GRADED_EXAM_MODAL_VARIANT.EXAM_TIMEOUT] ?? {};
    const gradedExamSubmitProps =
      GRADED_EXAM_MODAL_VARIANT_PROPS[GRADED_EXAM_MODAL_VARIANT.EXAM_SUBMIT] ?? {};

    const errorInfo = {
      message: 'ขออภัย ไม่สามารถตรวจแบบฝึกหัดได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
      onContinue: () => {
        if (secondsRemaining === 0) {
          setStatus(SESSION_STATUS.READY_TO_GRADE);
        } else {
          setStatus(SESSION_STATUS.CONFIRMING_ANSWER);
        }
      },
    };

    const handleStartSession = React.useCallback(async (examSessionId) => {
      try {
        const data = await client.query({
          query: getClassroomQuizExamPaperById,
          variables: { examSessionId },
        });

        const questions = delve(data, ['data', 'classroomQuizExamPaper', 'examPaperItemViews'], []);
        const formattedQuestions = questions.map((q) => {
          const { id, question, type, metadataV2, passage } = q;
          const choices = metadataV2?.choices ?? [];
          const precisionNumber = metadataV2?.precision ?? 0;
          const formattedChoices = choices.map((c) => {
            return { label: c.label, value: c.value, id: c.value };
          });

          return {
            id,
            content: question,
            choices: formattedChoices,
            precisionNumber,
            type,
            passage,
          };
        });
        const totalQuestions = delve(
          data,
          ['data', 'classroomQuizExamPaper', 'totalQuestions'],
          null
        );
        startSession({ totalQuestions, id: examSessionId }, formattedQuestions);
      } catch (err) {
        console.error(err);
        setError({
          message: 'ขออภัย ไม่สามารถทำแบบฝึกหัดได้ในขณะนี้ กรุณาลองใหม่อีกครั้ง',
          onContinue: () => history.goBack(),
        });
      }
    }, []);

    const goToGradedExamResult = () => {
      if (typeof timeLimit === 'number' && typeof secondsRemaining === 'number') {
        setTimeSpent(timeLimit - secondsRemaining);
      }
      history.push(QUIZ_SUB_ROUTES.GRADED_EXAM_RESULT.getPath(examId, examSessionId));
    };

    const goToOfficialExamResult = () => {
      if (typeof timeLimit === 'number' && typeof secondsRemaining === 'number') {
        setTimeSpent(timeLimit - secondsRemaining);
      }
      history.push(QUIZ_SUB_ROUTES.OFFICIAL_EXAM_RESULT.getPath(examId, examSessionId));
    };

    const handleGradePaper = async () => {
      let shouldGoToSummary = true;
      const examPaperItemAnswers = questions.map(({ id, type, selectedAnswer }) => {
        let answer = selectedAnswer;
        if (
          Array.isArray(selectedAnswer) &&
          (type === EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT ||
            type === EXAM_PAPER_ITEM_TYPE.MULTIPLE_SELECT_WITH_CORRECT_ANSWERS)
        ) {
          answer = selectedAnswer.sort().join(',');
        }
        if (type === EXAM_PAPER_ITEM_TYPE.FILL_NUMBER && answer !== null && answer !== undefined) {
          answer = selectedAnswer.toString();
        }
        if (Array.isArray(selectedAnswer) && type === EXAM_PAPER_ITEM_TYPE.CONNECTION) {
          answer = selectedAnswer
            .filter((arr) => arr.some((val) => val.trim() !== '')) // Remove empty arrays
            .map((arr) => arr.join('')) // Convert each array to a string
            .join(',');
        }
        return { examPaperItemId: id, answer: { value: answer || null } };
      });
      return client
        .mutate({
          mutation: submitClassroomQuizAnswers,
          variables: {
            examSessionId,
            examPaperItemAnswers,
          },
        })
        .then((res) => {
          /* Can't catch error from GraphQL because of response data
            Example response:
            "data":{
              "submitClassroomQuizAnswers": {
                "code": "403",
                "success": false,
                "message": "INVALID_TOKEN",
                "examPaperItems": null,
                "__typename": "ClassroomQuizSubmitAnswersResponse"
              }
            }
         */
          const dataAnswersSubmit = res?.data?.submitClassroomQuizAnswers;

          if (!dataAnswersSubmit?.success) throw dataAnswersSubmit;

          return client
            .mutate({
              mutation: gradeClassroomQuizExamPaper,
              variables: { examSessionId },
            })
            .then(() => {
              logSubmitQuiz({
                quizId: examId,
                subsectionTitle: title,
                subsectionContentType: type,
              });
            })
            .catch((err) => {
              console.error(err);
              shouldGoToSummary = false;
              logGradePaperError(
                `appVersion: ${appVersion} | id: ${id} | providerName: ${providerName} | examSessionId: ${examSessionId} | description: ${getError(
                  err
                )} `
              );
              setError(errorInfo);
            });
        })
        .catch((err) => {
          console.error(err);
          logSubmitQuizError({
            quizId: examId,
            errorMessage: err.message,
            subsectionTitle: title,
            subsectionContentType: type,
          });
          if (['403', 'FORBIDDEN'].includes(err?.code)) {
            logFinalAnswersSubmitError(
              `appVersion: ${appVersion} | id: ${id} | providerName: ${providerName} | examSessionId: ${examSessionId} | description: ${err.message}`
            );
            setError({
              message: t('MODAL.SUBMIT_QUIZ_CONCURRENT_ACCESS_ERROR.SUBTITLE'),
              textButton: t('MODAL.SUBMIT_QUIZ_CONCURRENT_ACCESS_ERROR.BUTTON'),
              onContinue: () => window.location.replace('/my-courses'),
            });
          } else if (['401', 'UNAUTHORIZED', 'UNAUTHENTICATED'].includes(err?.code)) {
            logFinalAnswersSubmitError(
              `appVersion: ${appVersion} | id: ${id} | providerName: ${providerName} | examSessionId: ${examSessionId} | description: ${err.message}`
            );
            setError({
              message: t('MODAL.SUBMIT_QUIZ_UNAUTHORIZATION_ERROR.SUBTITLE'),
              textButton: t('MODAL.SUBMIT_QUIZ_UNAUTHORIZATION_ERROR.BUTTON'),
              onContinue: () => window.location.replace('/'),
            });
          } else {
            logFinalAnswersSubmitError(
              `appVersion: ${appVersion} | id: ${id} | providerName: ${providerName} | examSessionId: ${examSessionId} | description: ${getError(
                err
              )} `
            );
            setError(errorInfo);
          }
          shouldGoToSummary = false;
        })
        .then(() => {
          typeof onCompleted === 'function' && onCompleted();
        })
        .then(() => {
          if (shouldGoToSummary) {
            switch (variant) {
              case QUIZ_VARIANTS.GRADED_EXAM: {
                return showResult ? gradePaper() : goToGradedExamResult();
              }
              case QUIZ_VARIANTS.OFFICIAL_EXAM: {
                return goToOfficialExamResult();
              }
              default: {
                return gradePaper();
              }
            }
          }
        });
    };
    const goToReviewAnswer = () => {
      // See issue ANY-267 for information about these code.
      if (location.pathname !== SESSION_SUB_ROUTES.REVIEW_ANSWER.getPath(examId, examSessionId)) {
        history.push(SESSION_SUB_ROUTES.REVIEW_ANSWER.getPath(examId, examSessionId));
      }
    };
    const handleTimeout = () => {
      setStatus(SESSION_STATUS.READY_TO_GRADE);
      goToReviewAnswer();
    };

    const handleSubmitGradedExam = () => {
      setStatus(SESSION_STATUS.GRADE);
    };

    const backToFirstQuestion = () => {
      setCurrentQuestionIndex(0);
      setRequestSubmitGradedExam(false);
    };

    React.useEffect(() => {
      switch (status) {
        case SESSION_STATUS.NOT_STARTED:
          handleStartSession(examSessionId);
          break;
        case SESSION_STATUS.IN_PROGRESS:
          history.push(SESSION_SUB_ROUTES.SESSION.getPath(examId, examSessionId));
          break;
        case SESSION_STATUS.CONFIRMING_ANSWER:
          goToReviewAnswer();
          break;
        case SESSION_STATUS.GRADE:
          handleGradePaper();
          break;
        case SESSION_STATUS.GRADED:
          history.push(QUIZ_SUB_ROUTES.RESULT.getPath(examId, examSessionId));
          clear();
          break;
        case SESSION_STATUS.READY_TO_GRADE:
        case SESSION_STATUS.TIMEOUT:
          break;
        default:
          clear();
          history.push(QUIZ_SUB_ROUTES.LANDING.getPath(examId));
          break;
      }
    }, [status]);

    return status === SESSION_STATUS.NOT_STARTED ? (
      <div>loading</div>
    ) : (
      <Fragment>
        <BlockNavigationDialog
          when={
            (status === SESSION_STATUS.IN_PROGRESS &&
              location.pathname === SESSION_SUB_ROUTES.SESSION.getPath(examId, examSessionId)) ||
            (status === SESSION_STATUS.CONFIRMING_ANSWER &&
              location.pathname === SESSION_SUB_ROUTES.REVIEW_ANSWER.getPath(examId, examSessionId))
          }
          message={'Are you sure?'}
        />
        {variant === QUIZ_VARIANTS.GRADED_EXAM && (
          <GradedExamModal
            isOpen={hasRequestSubmitGradedExam}
            primaryButtonLabel={'ส่งคำตอบ'}
            onPrimaryButtonClick={handleSubmitGradedExam}
            secondaryButtonLabel={'ทวนอีกครั้ง'}
            onSecondaryButtonClick={backToFirstQuestion}
            {...gradedExamSubmitProps}
          />
        )}
        {variant === QUIZ_VARIANTS.GRADED_EXAM ? (
          <GradedExamModal
            isOpen={status === SESSION_STATUS.TIMEOUT}
            primaryButtonLabel={'ส่งคำตอบ'}
            onPrimaryButtonClick={handleGradePaper}
            {...gradedExamTimeoutProps}
          />
        ) : (
          <TimeoutBlockNavigationDialog
            disableBackdropClick
            disableEscapeKeyDown
            open={status === SESSION_STATUS.TIMEOUT}
            onAcceptTimeout={handleTimeout}
          />
        )}

        {Object.keys(SESSION_SUB_ROUTES).map((key) => {
          const route = SESSION_SUB_ROUTES[key];
          const { exact, path, component: Component } = route;
          return (
            <Route
              key={path}
              exact={exact}
              path={path}
              onEndQuiz={onEndQuiz}
              render={(props) => <Component onEndQuiz={onEndQuiz} {...props} />}
            />
          );
        })}
      </Fragment>
    );
  }
);

const SessionRouteWrapper = withApollo(({ client, match, id, providerName, ...restProps }) => {
  const { examSessionId } = match.params;
  const {
    metadata: { timeLimit },
  } = useQuizContext();
  const { logAnswerSubmitError } = useQuizExceptionTracking();
  const { deviceInfo } = useCapacitor();
  const appVersion = deviceInfo.appVersion;

  const handleSubmitAnswer = async (question, answer) => {
    const { id } = question;
    try {
      await client.mutate({
        mutation: submitClassroomQuizAnswer,
        variables: {
          examSessionId,
          examPaperItemId: id,
          answer: { value: answer },
        },
      });
    } catch (err) {
      console.error(err);
      logAnswerSubmitError(
        `appVersion: ${appVersion} | id: ${id} | providerName: ${providerName} | examSessionId: ${examSessionId} | examPaperItemId: ${id} | description: ${getError(
          err
        )}`
      );
    }
  };
  return (
    <AssessmentProvider onSubmitAnswer={handleSubmitAnswer} timeLimit={timeLimit}>
      <Switch>
        <SessionRoute
          match={match}
          appVersion={appVersion}
          id={id}
          providerName={providerName}
          {...restProps}
        />
      </Switch>
    </AssessmentProvider>
  );
});

const SessionRouteProviderWrapper = (props) => {
  return (
    <QueryComponent query={getMyExternalProfileInfo}>
      {({ myExternalProfile = {} }) => (
        <QueryComponent query={getProfile} myExternalProfile={myExternalProfile}>
          {({ myProfile = {} }) => {
            const { id } = myProfile;
            const { providerName } = myExternalProfile ?? {};
            return <SessionRouteWrapper id={id} providerName={providerName} {...props} />;
          }}
        </QueryComponent>
      )}
    </QueryComponent>
  );
};

export default compose(withTranslation('classroom'))(SessionRouteProviderWrapper);
